import React, { useCallback, useState } from "react";
import {
  Link,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import {
  BiLike,
  BiPackage,
  BiBadgeCheck,
  BiXCircle,
  BiSolidTruck,
  BiRupee,
  BiUndo,
  BiBlock,
} from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import { ORDER_STATUS_CHANGE } from "../../../actions/admin/customer-actions";
import { normalizeOrderStatus } from "../../../utils/orderListTable";
import {
  hasStoredPaymentDetails,
} from "../../../utils/paymentDisplay";
import PaymentProofModal from "../../modals/payment-proof/PaymentProofModal";
import PaymentDetailsViewModal from "../../modals/payment-details-view/PaymentDetailsViewModal";

const PAYMENT_PROOF_STATUSES = new Set(["paid", "partial_paid", "unpaid"]);

export default function OrderStatusActions({ row, recordList }) {
  const profileDetails = useSelector((state) => state?.auth?.profileDetails);
  const isAdmin = profileDetails.user_type === "admin";
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const dispatch = useDispatch();
  const activeStatus = normalizeOrderStatus(row?.activeStatus);
  const hasItems = Array.isArray(row?.items) && row.items.length > 0;

  // Payment proof modal state
  const [proofModal, setProofModal] = useState({ open: false, payload: null, label: "" });
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  const paymentStatusKey = String(row.payment_status || "").toLowerCase();
  const hasPaymentDetails = hasStoredPaymentDetails(row, []) ||
    ["paid", "partial_paid", "unpaid", "not_paid"].includes(paymentStatusKey);
  const hasManualProofOnRecord =
    Boolean(row.payment_proof || row.reference_number || row.payment_note);

  const changeCustomeStatus = useCallback(
    (data) => {
      dispatch({
        type: ORDER_STATUS_CHANGE,
        payload: {
          ...data,
          onSuccess: () => {
            recordList();
            handleClose();
          },
        },
      });
    },
    [dispatch, recordList]
  );

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const withOrder = (payload) => ({ id: row.id, items: row.items, ...payload });

  // For statuses that need proof — open modal first
  const handlePaymentAction = (payload, label) => {
    const effectiveStatus = payload.payment_status || payload.status;
    if (PAYMENT_PROOF_STATUSES.has(effectiveStatus)) {
      handleClose();
      setProofModal({ open: true, payload, label });
    } else {
      changeCustomeStatus(withOrder(payload));
    }
  };

  const handleProofConfirm = ({ payment_proof, reference_number, payment_note }) => {
    changeCustomeStatus(
      withOrder({ ...proofModal.payload, payment_proof, reference_number, payment_note })
    );
  };

  const showFullOrderReject = !["approved", "bill_generated", "paid", "packing"].includes(activeStatus);

  return (
    <>
      <Tooltip
        title={!hasItems ? "No items in this order. The status cannot be changed." : ""}
        arrow
      >
        <span>
          <Link
            component="button"
            underline="none"
            color={hasItems ? "primary" : "text.disabled"}
            onClick={hasItems ? handleClick : undefined}
            sx={{ pointerEvents: hasItems ? "auto" : "none", opacity: hasItems ? 1 : 0.45 }}
          >
            VIEW
          </Link>
        </span>
      </Tooltip>

      {isAdmin && (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          PaperProps={{ elevation: 3, sx: { minWidth: 200, borderRadius: 2, mt: 1 } }}
        >
          {hasPaymentDetails && (
            <MenuItem
              onClick={() => {
                handleClose();
                setDetailsModalOpen(true);
              }}
            >
              <ListItemIcon><BiRupee size={20} /></ListItemIcon>
              <ListItemText primary="View Payment Details" />
            </MenuItem>
          )}

          {activeStatus === "pending" && (
            <MenuItem onClick={() => changeCustomeStatus(withOrder({ status: "approved" }))}>
              <ListItemIcon><BiLike size={20} /></ListItemIcon>
              <ListItemText primary="Approve" />
            </MenuItem>
          )}

          {activeStatus === "approved" && (
  <>
    {/* Paid - Keep disabled */}
    <Tooltip
      title="Send the final bill first to enable payment actions"
      placement="left"
      arrow
    >
      <span>
        <MenuItem disabled>
          <ListItemIcon>
            <BiRupee size={20} />
          </ListItemIcon>
          <ListItemText primary="Paid" />
        </MenuItem>
      </span>
    </Tooltip>

    {/* Partial Paid - Keep disabled */}
    <Tooltip
      title="Send the final bill first to enable payment actions"
      placement="left"
      arrow
    >
      <span>
        <MenuItem disabled>
          <ListItemIcon>
            <BiRupee size={20} />
          </ListItemIcon>
          <ListItemText primary="Partial Paid" />
        </MenuItem>
      </span>
    </Tooltip>

    {/* Unpaid - Enabled */}
    {!(["paid", "partial_paid"].includes(paymentStatusKey) || hasManualProofOnRecord) && (
      <MenuItem
        onClick={() =>
          handlePaymentAction(
            { payment_status: "unpaid" },
            "Unpaid"
          )
        }
      >
        <ListItemIcon>
          <BiRupee size={20} />
        </ListItemIcon>
        <ListItemText primary="Unpaid" />
      </MenuItem>
    )}
  </>
)}

          {activeStatus === "bill_generated" && (
            <>
              <MenuItem onClick={() => handlePaymentAction({ status: "paid" }, "Paid")}>
                <ListItemIcon><BiRupee size={20} /></ListItemIcon>
                <ListItemText primary="Paid" />
              </MenuItem>
              <MenuItem onClick={() => handlePaymentAction({ payment_status: "partial_paid" }, "Partial Paid")}>
                <ListItemIcon><BiRupee size={20} /></ListItemIcon>
                <ListItemText primary="Partial Paid" />
              </MenuItem>
              {!(["paid", "partial_paid"].includes(paymentStatusKey) || hasManualProofOnRecord) && (
                <MenuItem onClick={() => handlePaymentAction({ payment_status: "unpaid" }, "Unpaid")}>
                  <ListItemIcon><BiRupee size={20} /></ListItemIcon>
                  <ListItemText primary="Unpaid" />
                </MenuItem>
              )}
              <MenuItem onClick={() => changeCustomeStatus(withOrder({ payment_status: "reject_shipment" }))}>
                <ListItemIcon><BiBlock size={20} /></ListItemIcon>
                <ListItemText primary="Reject Shipment" />
              </MenuItem>
            </>
          )}

          {activeStatus === "paid" && (
            <>
              <MenuItem onClick={() => changeCustomeStatus(withOrder({ status: "packing" }))}>
                <ListItemIcon><BiPackage size={20} /></ListItemIcon>
                <ListItemText primary="Packed" />
              </MenuItem>
              <MenuItem onClick={() => changeCustomeStatus(withOrder({ payment_status: "unpacked" }))}>
                <ListItemIcon><BiUndo size={20} /></ListItemIcon>
                <ListItemText primary="Unpacked" />
              </MenuItem>
              <MenuItem onClick={() => changeCustomeStatus(withOrder({ payment_status: "reject_shipment" }))}>
                <ListItemIcon><BiBlock size={20} /></ListItemIcon>
                <ListItemText primary="Reject Shipment" />
              </MenuItem>
            </>
          )}

          {activeStatus === "packing" && (
            <>
              <MenuItem onClick={() => changeCustomeStatus(withOrder({ status: "shipment" }))}>
                <ListItemIcon><BiSolidTruck size={20} /></ListItemIcon>
                <ListItemText primary="Shipped" />
              </MenuItem>
              <MenuItem onClick={() => changeCustomeStatus(withOrder({ payment_status: "reject_shipment" }))}>
                <ListItemIcon><BiBlock size={20} /></ListItemIcon>
                <ListItemText primary="Reject Shipment" />
              </MenuItem>
            </>
          )}

          {activeStatus === "shipment" && (
            <MenuItem onClick={() => changeCustomeStatus(withOrder({ status: "delivered" }))}>
              <ListItemIcon><BiBadgeCheck size={20} /></ListItemIcon>
              <ListItemText primary="Delivered" />
            </MenuItem>
          )}

          {showFullOrderReject && (
            <MenuItem onClick={() => changeCustomeStatus(withOrder({ status: "rejected" }))}>
              <ListItemIcon><BiXCircle size={20} /></ListItemIcon>
              <ListItemText primary="Rejected" />
            </MenuItem>
          )}
        </Menu>
      )}

      <PaymentProofModal
        open={proofModal.open}
        label={proofModal.label}
        onClose={() => setProofModal({ open: false, payload: null, label: "" })}
        onConfirm={handleProofConfirm}
      />

      <PaymentDetailsViewModal
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        order={row}
      />

    </>
  );
}
